// src/admin/AdminLogin.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { Leaf, Lock, Mail, Eye, EyeOff } from 'lucide-react';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [siteLogo, setSiteLogo] = useState('');
  const [siteName, setSiteName] = useState('NAVIROSE');
  const navigate = useNavigate();

  // Fetch site settings (logo + name)
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const docRef = doc(db, 'settings', 'site');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.logo) setSiteLogo(data.logo);
          if (data.siteName) setSiteName(data.siteName);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        setError('User record not found in Firestore.');
        await auth.signOut();
        setLoading(false);
        return;
      }

      const userData = userDocSnap.data();
      const validRoles = ['superghost', 'superadmin', 'admin', 'manager', 'employee'];

      if (!validRoles.includes(userData.role)) {
        setError(`Access denied. Your role is "${userData.role || 'unknown'}".`);
        await auth.signOut();
        setLoading(false);
        return;
      }

      localStorage.setItem('adminRole', userData.role);
      localStorage.setItem('adminName', userData.name || 'Admin');

      navigate('/admin/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        setError('Invalid email or password.');
      } else if (err.code === 'auth/user-not-found') {
        setError('No user found with this email.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email format.');
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        background: `linear-gradient(135deg, var(--color-button-bg) 0%, var(--color-button-hover-bg) 100%)`,
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">

        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          {siteLogo ? (
            <img src={siteLogo} alt={siteName} className="h-16 w-auto max-w-[180px] object-contain mb-3" />
          ) : (
            <>
              <div className="rounded-full p-4 mb-3" style={{ backgroundColor: '#FFF2F6' }}>
                <Leaf size={40} style={{ color: 'var(--color-link)' }} />
              </div>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--color-primary-text)' }}>Admin Panel</h1>
            </>
          )}
          <p className="text-sm mt-1" style={{ color: 'var(--color-secondary-text)' }}>
            {siteName} Management System
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-4">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleLogin} className="space-y-4">

          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-primary-text)' }}>Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-3.5 text-gray-400" size={18} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@navirose.com"
                required
                className="w-full border rounded-lg py-3 px-4 pl-11 focus:outline-none text-sm"
                style={{ borderColor: 'var(--color-border)' }}
                onFocus={(e) => e.target.style.borderColor = 'var(--color-button-bg)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-primary-text)' }}>Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-3.5 text-gray-400" size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="w-full border rounded-lg py-3 px-4 pl-11 pr-11 focus:outline-none text-sm"
                style={{ borderColor: 'var(--color-border)' }}
                onFocus={(e) => e.target.style.borderColor = 'var(--color-button-bg)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 rounded-lg font-semibold transition flex items-center justify-center gap-2 btn-2d text-white ${
              loading ? 'opacity-70 cursor-not-allowed' : ''
            }`}
            style={{ backgroundColor: 'var(--color-button-bg)' }}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                Logging in...
              </>
            ) : (
              'Login to Admin Panel'
            )}
          </button>

        </form>

        <p className="text-center text-xs text-gray-400 mt-6">
          © 2026 {siteName}. All rights reserved.
        </p>

      </div>
    </div>
  );
};

export default AdminLogin;